import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, UserPlus, IndianRupee, TrendingUp, ShoppingCart, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, LineChart, Line } from "recharts";
import { DashShell, StatCard, Badge } from "@/components/dash/DashShell";
import { ADMIN_NAV } from "./nav";
import { api, inr } from "@/lib/api";

const COLORS = ["#0E4B43", "#E5C378", "#10B981", "#14655a", "#F59E0B", "#268274"];

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [orders, setOrders] = useState([]);
    useEffect(() => {
        api.get("/admin/stats").then(({ data }) => setStats(data));
        api.get("/admin/leads").then(({ data }) => setLeads(data.slice(0, 6)));
        api.get("/admin/orders").then(({ data }) => setOrders(data.slice(0, 6)));
    }, []);

    return (
        <DashShell nav={ADMIN_NAV} title="Dashboard">
            {!stats ? <Loading /> : (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard label="Revenue" value={inr(stats.revenue)} icon={IndianRupee} accent="gold" testid="stat-revenue" />
                        <StatCard label="Paid Orders" value={stats.paid_orders} icon={ShoppingCart} accent="green" testid="stat-orders" />
                        <StatCard label="Total Leads" value={stats.total_leads} icon={Users} testid="stat-leads" />
                        <StatCard label="Conversion Rate" value={`${stats.conversion_rate}%`} icon={TrendingUp} accent="green" testid="stat-conversion" />
                        <StatCard label="New Leads" value={stats.new_leads} icon={UserPlus} accent="teal" />
                        <StatCard label="Assigned" value={stats.assigned_leads} icon={Users} accent="teal" />
                        <StatCard label="In Progress" value={stats.in_progress} icon={Clock} accent="gold" />
                        <StatCard label="Pending Follow-ups" value={stats.pending_followups} icon={Clock} accent="red" />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card title="Leads by Day">
                            <div className="h-56">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={stats.leads_by_day}><XAxis dataKey="date" fontSize={11} tickFormatter={(d) => d?.slice(5)} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Line type="monotone" dataKey="value" stroke="#0E4B43" strokeWidth={2.5} /></LineChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                        <Card title="Orders by Service">
                            <div className="h-56">
                                {stats.orders_by_service.length ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={stats.orders_by_service}><XAxis dataKey="name" fontSize={10} interval={0} /><YAxis fontSize={11} allowDecimals={false} /><Tooltip /><Bar dataKey="value" fill="#E5C378" radius={[6, 6, 0, 0]} /></BarChart>
                                    </ResponsiveContainer>
                                ) : <Empty />}
                            </div>
                        </Card>
                        <Card title="Revenue by Service">
                            <div className="h-56">
                                {stats.revenue_by_service.length ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={stats.revenue_by_service} layout="vertical"><XAxis type="number" fontSize={11} /><YAxis type="category" dataKey="name" fontSize={10} width={110} /><Tooltip formatter={(v) => inr(v)} /><Bar dataKey="value" fill="#10B981" radius={[0, 6, 6, 0]} /></BarChart>
                                    </ResponsiveContainer>
                                ) : <Empty />}
                            </div>
                        </Card>
                        <Card title="Lead Sources">
                            <div className="h-56">
                                {stats.lead_sources.length ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart><Pie data={stats.lead_sources} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>{stats.lead_sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
                                    </ResponsiveContainer>
                                ) : <Empty />}
                            </div>
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        <Card title="Recent Leads" action={<Link to="/admin/leads" className="text-sm text-teal-700 hover:underline">View all</Link>}>
                            <div className="divide-y divide-black/5">
                                {leads.map((l) => (
                                    <Link key={l.lead_id} to={`/admin/leads/${l.lead_id}`} className="flex items-center justify-between py-3 hover:bg-black/[0.02] px-2 rounded-lg" data-testid={`recent-lead-${l.lead_id}`}>
                                        <div><div className="font-medium text-teal-900 text-sm">{l.customer_name}</div><div className="text-xs text-teal-900/50">{l.business_name || l.service || "—"} · {l.city || "—"}</div></div>
                                        <Badge status={l.lead_status} />
                                    </Link>
                                ))}
                                {!leads.length && <Empty />}
                            </div>
                        </Card>
                        <Card title="Recent Orders" action={<Link to="/admin/orders" className="text-sm text-teal-700 hover:underline">View all</Link>}>
                            <div className="divide-y divide-black/5">
                                {orders.map((o) => (
                                    <div key={o.order_id} className="flex items-center justify-between py-3 px-2">
                                        <div><div className="font-medium text-teal-900 text-sm">{o.customer_name}</div><div className="text-xs text-teal-900/50 font-mono">{o.order_id} · {inr(o.amount)}</div></div>
                                        <Badge status={o.payment_status} />
                                    </div>
                                ))}
                                {!orders.length && <Empty />}
                            </div>
                        </Card>
                    </div>
                </div>
            )}
        </DashShell>
    );
}

function Card({ title, children, action }) {
    return (
        <div className="bg-white rounded-2xl border border-black/5 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-4"><h3 className="font-semibold text-teal-900">{title}</h3>{action}</div>
            {children}
        </div>
    );
}
function Loading() { return <div className="grid place-items-center py-32 text-teal-900/50"><Loader2 className="w-8 h-8 animate-spin" /></div>; }
function Empty() { return <div className="grid place-items-center h-full py-10 text-teal-900/40 text-sm">No data yet</div>; }
